import { useState, useMemo, useCallback } from 'react';
import { ParameterTemplate } from '../../components/workflows/nodes/config/parameterTemplates';
import { isValidEmailForSending } from '../../utils/validation';

export const useParameterValidation = (template: ParameterTemplate) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validationRules = useMemo(() => {
    const rules: Record<string, Array<(value: any) => string | null>> = {};

    Object.entries(template).forEach(([key, config]) => {
      rules[key] = [];

      // Required validation
      if (config.required) {
        rules[key].push((value: any) => {
          if (!value || (typeof value === 'string' && !value.trim())) {
            return `${config.label} is required`;
          }
          return null;
        });
      }

      // Email validation - uses robust validation from utils
      if (config.type === 'email') {
        rules[key].push((value: any) => {
          if (value && !isValidEmailForSending(value)) {
            return 'Please enter a valid email address';
          }
          return null;
        });
      }

      // Number validation
      if (config.type === 'number') {
        rules[key].push((value: any) => {
          if (value && (isNaN(Number(value)) || Number(value) < 0)) {
            return 'Please enter a valid positive number';
          }
          return null;
        });
      }

      // Range validation
      if (config.min !== undefined || config.max !== undefined) {
        rules[key].push((value: any) => {
          // Skip range validation for empty values
          if (!value || (typeof value === 'string' && !value.trim())) {
            return null;
          }
          
          const num = Number(value);
          if (!isNaN(num)) {
            if (config.min !== undefined && num < config.min) {
              return `Value must be at least ${config.min}`;
            }
            if (config.max !== undefined && num > config.max) {
              return `Value must be at most ${config.max}`;
            }
          }
          return null;
        });
      }
    });

    return rules;
  }, [template]);

  const validateParameters = useCallback((parameters: Record<string, any>): boolean => {
    const newErrors: Record<string, string> = {};

    Object.entries(validationRules).forEach(([key, rules]) => {
      const value = parameters[key];

      for (const rule of rules) {
        const error = rule(value);
        if (error) {
          newErrors[key] = error;
          break; // Stop at first error
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [validationRules]);

  const clearError = useCallback((key: string) => {
    setErrors(prev => {
      if (!prev[key]) return prev;
      const { ...rest } = { ...prev };
      delete rest[key];
      return rest;
    });
  }, []);

  return {
    errors,
    validateParameters,
    clearError,
    setErrors
  };
};